import { fetchGracenoteLineups } from 'core/API/GameMasterLineup';
import React, { 
    useEffect, 
    useState, 
    useCallback, 
    useRef, 
    useMemo 
} from 'react';
import Select from 'react-select';
import styles from './GamemasterProvider.module.scss';
import { Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, CircularProgress } from '@mui/material';
import { fetchChannels, fetchGracenoteChannels, fetchPreferenceChannels, useChannelsMutation, deletePreferenceChannelsByLineup, getAdditionalChannelLookup } from 'core/API/gameMasterSports';
import { ChannelSelector } from './ChannelSelector/ChannelSelector';
import Cookies from 'js-cookie';
import { fetchTvPackages, fetchTvAddons, TvPackage, TvAddon, clearTvPackagesCache, clearTvAddonsCache } from 'core/API/GameMasterData';
import { clearPreferenceChannelsCache, clearTvPackageChannelsCache } from 'core/API/gameMasterSports';
import { clearLineupsCache } from 'core/API/GameMasterLineup';
import { useQueryClient, useQuery } from 'react-query';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import { apiService } from 'core/services/apiService';
import { checkAndAutoActivateScheduling, callActivateDeactivateScheduling, DEACTIVATE_SCHEDULING_CONFIRM_MESSAGE } from 'core/API/gameMasterScheduling';
import { RebuildSchedule } from 'core/API/gameMaster';
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

interface Channel {
    id: number;
    lineup: string;
    channel: string;
    call_sign?: string | null;
    station_id?: string | null;
    // New API field names
    callSign?: string | null;
    stationId?: string | null;
    // DirectTV package/addon fields
    lineupPackage?: string;
    lineupAddon?: string;
}

interface LineupDetail {
    type: string;
    device?: string;
    lineupId: string;
    name: string;
    location?: string;
    mso?: { id: string; name: string };
}

interface GamemasterProviderProps {
    onViewModeChange?: (mode: 'list' | 'create' | 'edit' | 'view') => void;
    canEdit?: boolean;
    canDelete?: boolean;
    canExecute?: boolean;
}

const GamemasterProvider: React.FC<GamemasterProviderProps> = ({
    onViewModeChange,
    canEdit = false,
    canDelete = false,
    canExecute = false,
}) => {
    const queryClient = useQueryClient();
    const { workplaceId } = useWorkplacesContext();
    // Accordion always open - no state needed
    const [viewMode, setViewMode] = useState<'list' | 'create' | 'edit' | 'view'>('list');
    const [selectedProvider, setSelectedProvider] = useState<LineupDetail | null>(null);
    const [lineup, setLineup] = useState<any[]>([]);
    const [selectedLineups, setSelectedLineups] = useState<LineupDetail[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [directTvEspn, setDirectTvEspn] = useState(false);
    const [channels, setChannels] = useState<any[]>([]); // Channels from fetchGracenoteChannels
    const [tvPackageChannels, setTvPackageChannels] = useState<any[]>([]); // Channels from tv_package_channels API
    const [savedChannelMap, setSavedChannelMap] = useState<Record<string, string[]>>({});
    const [savedPackageMap, setSavedPackageMap] = useState<Record<string, number | null>>({});
    const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true); // New state for initial loading
    const [isLoadingChannels, setIsLoadingChannels] = useState<boolean>(false); // Loading state for channels API
    const [loadedLineupId, setLoadedLineupId] = useState<string | null>(null); // Track which lineup's channels are loaded
    const isApiCallingRef = useRef<boolean>(false); // Ref to prevent double API calls
    const [tvPackages, setTvPackages] = useState<TvPackage[]>([]);
    const [selectedTvPackage, setSelectedTvPackage] = useState<number | null>(null); // Radio button - single selection
    const [tvAddons, setTvAddons] = useState<TvAddon[]>([]);
    const [selectedTvAddons, setSelectedTvAddons] = useState<number[]>([]); // Only addons from selected package
    const [isLoadingTvPackages, setIsLoadingTvPackages] = useState(false);
    const [selectedPackageObj, setSelectedPackageObj] = useState<TvPackage | null>(null);
    const [isRebuilding, setIsRebuilding] = useState(false);
    const [isRebuildDialogOpen, setIsRebuildDialogOpen] = useState(false);
    const [activateScheduling, setActivateScheduling] = useState(false);
    const [isTogglingScheduling, setIsTogglingScheduling] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const [formValues, setFormValues] = useState<{
        channels: Record<string, string[]>;
    }>({
        channels: {},
    });

    // Notify parent of initial view mode
    useEffect(() => {
        onViewModeChange?.('list');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Preload tv_package_channels API on page load and store in localStorage
    useEffect(() => {
        const preloadTvPackageChannels = async () => {
            const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
            if (!workplaceIdValue) {
                return;
            }

            console.log('Preloading tv_package_channels API on page load...');
            try {
                // Call API with cache enabled - it will check localStorage first, then call API if needed
                // This ensures data is available in localStorage for later use
                await fetchChannels(workplaceIdValue, 2000, true);
                console.log('tv_package_channels preloaded and stored in localStorage');
            } catch (error) {
                console.error("Error preloading tv_package_channels:", error);
            }
        };

        // Call on component mount
        preloadTvPackageChannels();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterChannelNumbers = (
        channels: any[],
        selectedPackageId: number | null,
        selectedAddonIds: number[],
        ): Record<string, string[]> => {
        const filteredChannels = channels.filter((channel: any) => {
            // Include if channel belongs to selected package
            if (selectedPackageId && channel.tvPackage && channel.tvPackage.id === selectedPackageId) {
                return true;
            }
            // Include if channel belongs to selected addon
            // Check both the addon flag and tvAddon object
            if (channel.addon === true && channel.tvAddon && channel.tvAddon.id && selectedAddonIds.includes(channel.tvAddon.id)) {
                return true;
            }
            return false;
        });

        const totalFilteredCount = filteredChannels.length;

        // Group by lineup
        const grouped: Record<string, string[]> = filteredChannels.reduce((acc: Record<string, string[]>, item: any) => {
            // Use lineupId from tvPackage or tvAddon, or fallback to item.lineup
            const key = item.tvPackage?.lineupId || item.tvAddon?.lineupId || item.lineup;
            if (!key) return acc;
                    if (!acc[key]) acc[key] = [];
            const channelNumber = item.channel ? String(item.channel).trim() : '';
            if (channelNumber !== '') {
                // Avoid duplicates
                if (!acc[key].includes(channelNumber)) {
                    acc[key].push(channelNumber);
                }
                    }
                    return acc;
                }, {});

        // For DIRECTV lineups, use the total filtered count instead of grouped count
        // This ensures we show the exact filtered count (80) not grouped count (79)
        if (totalFilteredCount > 0) {
            Object.keys(grouped).forEach(key => {
                if (key.includes('DITV') || key.startsWith('USA-DITV')) {
                    // Store total filtered count - we'll use this for count display
                    // The actual channel numbers are in grouped[key], but count should be totalFilteredCount
                    // We'll add a special property to track the total count
                    const currentGroupedCount = grouped[key].length;
                    if (currentGroupedCount < totalFilteredCount) {
                        // Use total filtered count - add placeholder entries to match the count
                        // This ensures the count shows correctly (80) even if grouping loses some
                        const missingCount = totalFilteredCount - currentGroupedCount;
                        // Add unique placeholders to match the total count
                        for (let i = 0; i < missingCount; i++) {
                            grouped[key].push(`_count_${i}_`);
                        }
                    }
                }
            });
        }

        return grouped;
        };

    // Helper function to find DIRECTV channel key from filtered channels
    // This finds the channel key that contains DIRECTV channels (e.g., USA-DITV623-X)
    const findDirectTvChannelKey = (fChannels: Record<string, string[]>): string | null => {
        const channelKeys = Object.keys(fChannels);
        // Find any key that contains DITV pattern
        return channelKeys.find(key => 
            key.includes('DITV') || 
            key.startsWith('USA-DITV') ||
            key.includes('DTVNOW')
        ) || null;
    };

    // Helper function to match filtered channels to DIRECTV lineups dynamically
    // Channels might have lineupId like USA-DITV623-X while lineup has USA-DITV-X
    // This handles multiple DIRECTV lineups that can be selected at the same time
    const matchChannelsToLineup = (
        fChannels: Record<string, string[]>, 
        lineupId: string, 
        lineupName?: string
    ): string[] => {
        // Try exact match first
        let matchedChannels = fChannels[lineupId] || [];
        
        // If no exact match and lineup name starts with "DIRECT", try to find matching channel key
        if (matchedChannels.length === 0 && lineupName?.startsWith("DIRECT")) {
            // Find the DIRECTV channel key (works for all DIRECTV lineups)
            const directTvKey = findDirectTvChannelKey(fChannels);
            if (directTvKey) {
                matchedChannels = fChannels[directTvKey] || [];
            } else {
                // Fallback: try pattern matching
                const channelKeys = Object.keys(fChannels);
                const lineupBasePattern = lineupId.split('-').slice(0, 2).join('-'); // e.g., "USA-DITV"
                
                const matchingKey = channelKeys.find(key => {
                    const keyBasePattern = key.split('-').slice(0, 2).join('-');
                    return keyBasePattern === lineupBasePattern || 
                           (key.includes('DITV') && lineupBasePattern.includes('DITV')) ||
                           (key.startsWith('USA-DITV') && lineupId.startsWith('USA-DITV'));
                });
                
                if (matchingKey) {
                    matchedChannels = fChannels[matchingKey] || [];
                }
            }
        }
        
        return matchedChannels;
    };

    const getChannelNumber = (channelValue: string | number) => {
        const match = String(channelValue).match(/\d+/);
        if (!match) return null;
        const parsed = parseInt(match[0], 10);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const isRealChannelNumber = (channel: string) =>
        !!channel && String(channel).trim() !== "" && !String(channel).startsWith("_count_");

    /**
     * Apply package/addon membership changes without wiping manual selections.
     * - Drops channels that were in the previous filter but not the next (unchecked addon/package)
     * - Adds channels newly included in the next filter
     * - Keeps existing channels that were never part of the previous auto-filter (manual picks)
     */
    const applyMembershipDiff = (
        existingChannels: string[],
        previousFiltered: string[],
        nextFiltered: string[]
    ): string[] => {
        const prevSet = new Set(previousFiltered.filter(isRealChannelNumber).map(String));
        const nextSet = new Set(nextFiltered.filter(isRealChannelNumber).map(String));
        const removed = new Set([...prevSet].filter((ch) => !nextSet.has(ch)));

        const kept = existingChannels.filter(
            (ch) => isRealChannelNumber(ch) && !removed.has(String(ch))
        );
        const nextReal = nextFiltered.filter(isRealChannelNumber);

        return Array.from(new Set([...kept, ...nextReal]));
    };

    /** @deprecated use applyMembershipDiff — kept name for call-site clarity */
    const applyFilteredPackageChannels = (
        matchedChannels: string[],
        existingChannels: string[],
        previousFilteredMembership: string[] = []
    ) => applyMembershipDiff(existingChannels, previousFilteredMembership, matchedChannels);

    // Fetch game master settings for activateScheduling
    const fetchGameMasterSettings = async () => {
        const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
        if (!workplaceIdValue) return null;
        
        try {
            const res = await apiService.get(`/game_master_settings/${workplaceIdValue}`);
            let data = res.data;
            // Handle different response formats
            if (data?.member && Array.isArray(data.member) && data.member.length > 0) {
                data = data.member[0];
            }
            return data || null;
        } catch (error) {
            console.error("Error fetching game master settings:", error);
            return null;
        }
    };

    const { data: gameMasterSettings, refetch: refetchGameMasterSettings } = useQuery(
        ['gameMasterSettings', workplaceId],
        fetchGameMasterSettings,
        {
            enabled: !!workplaceId,
            retry: false,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
            onSuccess: (data) => {
                if (data) {
                    setActivateScheduling(Boolean(data.activateScheduling));
                } else {
                    setActivateScheduling(false);
                }
            },
        }
    );

    // Sync activateScheduling state with API response
    useEffect(() => {
        if (gameMasterSettings) {
            setActivateScheduling(Boolean(gameMasterSettings.activateScheduling));
        } else if (gameMasterSettings === null) {
            // Only set to false if we got a null response (not undefined/loading)
            setActivateScheduling(false);
        }
    }, [gameMasterSettings]);

    // Handle activate scheduling toggle
    const handleActivateSchedulingToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
        
        if (!workplaceIdValue) {
            setSnackbar({
                open: true,
                message: 'Workplace ID not found',
                severity: 'error',
            });
            return;
        }

        setIsTogglingScheduling(true);
        try {
            const response = await apiService.post(
                `/game_master_settings/${workplaceIdValue}/activate-scheduling`,
                { activateScheduling: newValue }
            );
            
            // Check if response indicates success
            if (response.data?.success === false) {
                // Show error message from API
                const errorMessage = response.data?.message || 'Failed to activate scheduling';
                setSnackbar({
                    open: true,
                    message: errorMessage,
                    severity: 'error',
                });
                // Don't update state if activation failed
                return;
            }
            
            // Success - update state and refetch
            setActivateScheduling(newValue);
            await refetchGameMasterSettings();
            
            setSnackbar({
                open: true,
                message: newValue ? 'Scheduling activated successfully' : 'Scheduling deactivated successfully',
                severity: 'success',
            });
        } catch (error: any) {
            console.error("Error toggling activate scheduling:", error);
            const errorMessage = error?.response?.data?.message || 
                                error?.message || 
                                'Failed to update scheduling status';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setIsTogglingScheduling(false);
        }
    };

    useEffect(() => {
        const GracenoteData = async () => {
            const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
            if (!workplaceIdValue) {
                setIsInitialLoading(false);
                return;
            }
            try {
                setIsInitialLoading(true); // Start initial loading
                
                // Load providers (lineups) first
                const res = await fetchGracenoteLineups(workplaceIdValue);
                
                // Set lineup first so it's available
                setLineup(res);

                // Load saved channel preferences from API - this is the main source of data for list view
                // Always call this API to get dynamic data from server
                const saved: any[] = await fetchPreferenceChannels(workplaceIdValue, 2000);
                
                // Don't preload channels - will be loaded on-demand with lineup ID in add/edit/view modes

                // Group saved channels by lineup id -> string[] of channel codes
                const grouped: Record<string, string[]> = saved.reduce((acc: Record<string, string[]>, item: any) => {
                    const key = item.lineup;
                    if (!acc[key]) acc[key] = [];
                    if (item.channel && String(item.channel).trim() !== '') {
                        acc[key].push(item.channel);
                    }
                    return acc;
                }, {});

                // Build package map by lineup id for accurate list counts
                const packageGrouped: Record<string, number | null> = saved.reduce((acc: Record<string, number | null>, item: any) => {
                    const key = item.lineup;
                    const packageStr = item.lineupPackage || item.lineup_package || '';
                    if (!(key in acc)) {
                        acc[key] = packageStr && String(packageStr).trim() !== '' ? parseInt(packageStr) : null;
                        if (acc[key] !== null && Number.isNaN(acc[key] as number)) {
                            acc[key] = null;
                        }
                    }
                    return acc;
                }, {});

                // Set savedChannelMap with data from API - this is the source of truth for list view
                setSavedChannelMap(grouped);
                setSavedPackageMap(packageGrouped);

                // Extract package and addon info for DirectTV lineups from saved channels
                // Check all DirectTV lineups to find saved package/addon info
                const directTvLineups = res.filter((l: any) => l.name?.startsWith("DIRECT"));
                if (directTvLineups.length > 0 && saved.length > 0) {
                    // Find any DirectTV lineup with saved package info
                    // All DirectTV lineups should use the same package/addon
                    const directTvSavedChannel = saved.find((item: any) => {
                        const lineup = directTvLineups.find((l: any) => l.lineupId === item.lineup);
                        const itemAny = item as any;
                        return lineup && itemAny.lineupPackage && String(itemAny.lineupPackage).trim() !== '';
                    }) as any;

                    if (directTvSavedChannel) {
                        // Set the saved package (applies to all DirectTV lineups)
                        const savedPackageStr = directTvSavedChannel.lineupPackage || '';
                        const savedPackageId = parseInt(savedPackageStr);
                        if (!isNaN(savedPackageId)) {
                            // Parse and set saved addons (comma-separated string)
                            let savedAddonIds: number[] = [];
                            const savedAddonStr = directTvSavedChannel.lineupAddon || '';
                            if (savedAddonStr && String(savedAddonStr).trim() !== '') {
                                savedAddonIds = String(savedAddonStr)
                                    .split(',')
                                    .map((id: string) => parseInt(id.trim()))
                                    .filter((id: number) => !isNaN(id));
                            }
                            
                            setSelectedTvPackage(savedPackageId);
                            if (savedAddonIds.length > 0) {
                                setSelectedTvAddons(savedAddonIds);
                            }
                            
                            // Mark all DirectTV lineups as needing initialization
                            // This ensures channels are filtered for all lineups when they load
                            directTvLineups.forEach((lineup) => {
                                channelsInitializedRef.current.delete(`${lineup.lineupId}-${savedPackageId}`);
                            });
                            
                            // Channels will be loaded when user enters add/edit mode
                            // Don't filter here in list mode - channels API not called
                            // Just update refs to track saved package/addon
                            prevPackageRef.current = savedPackageId;
                            prevAddonsRef.current = savedAddonIds;
                        }
                    }
                }

                // Derive selected lineups from any lineup keys that have saved channels
                const selectedFromSaved: LineupDetail[] = res
                    .filter((l: any) => grouped[l.lineupId]?.length)
                    .map((l: any) => ({
                        type: l.type,
                        device: l.device,
                        lineupId: l.lineupId,
                        name: l.name,
                        location: l.location,
                        mso: l.mso,
                    }));
                
                // Initialize formValues with saved channels (prefill)
                setFormValues({
                    channels: grouped,
                });
                // Initialize selectedLineups from saved selections
                setSelectedLineups(selectedFromSaved);
            } catch (error) {
                console.error("Error fetching Gracenote data:", error);
                setErrorMessage("Failed to load provider data.");
            } finally {
                setIsInitialLoading(false); // End initial loading
            }
        };
        GracenoteData();
    }, [workplaceId]);

    // Call preference channels API whenever lineup records are available (refresh data in list view)
    // This ensures data is always fresh from API, not from cache or localStorage
    useEffect(() => {
        const loadPreferenceChannels = async () => {
            // Only call if lineup has records and we're in list mode
            if (lineup.length === 0 || viewMode !== 'list') {
                return;
            }
            
            const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
            if (!workplaceIdValue) {
                return;
            }
            
            try {
                // Always call preference channels API to get latest dynamic data from server
                // This is the source of truth for list view - all data comes from API
                const saved: any[] = await fetchPreferenceChannels(workplaceIdValue, 2000);
                
                // Group saved channels by lineup id -> string[] of channel codes
                const grouped: Record<string, string[]> = saved.reduce((acc: Record<string, string[]>, item: any) => {
                    const key = item.lineup;
                    if (!acc[key]) acc[key] = [];
                    if (item.channel && String(item.channel).trim() !== '') {
                        acc[key].push(item.channel);
                    }
                    return acc;
                }, {});

                // Always replace savedChannelMap with fresh data from API (dynamic data, not cached)
                setSavedChannelMap(grouped);

                // Also update package map for list counts
                const packageGrouped: Record<string, number | null> = saved.reduce((acc: Record<string, number | null>, item: any) => {
                    const key = item.lineup;
                    const packageStr = item.lineupPackage || item.lineup_package || '';
                    if (!(key in acc)) {
                        acc[key] = packageStr && String(packageStr).trim() !== '' ? parseInt(packageStr) : null;
                        if (acc[key] !== null && Number.isNaN(acc[key] as number)) {
                            acc[key] = null;
                        }
                    }
                    return acc;
                }, {});
                setSavedPackageMap(packageGrouped);
            } catch (error) {
                console.error("Error fetching preference channels:", error);
            }
        };
        
        // Call immediately when lineup is available and in list mode
        loadPreferenceChannels();
    }, [lineup, workplaceId, viewMode]);

    // Store previous package/addon to detect changes
    const prevPackageRef = useRef<number | null>(null);
    const prevAddonsRef = useRef<number[]>([]);
    const channelsInitializedRef = useRef<Set<string>>(new Set()); // Track which lineups have been initialized
    const isHydratingDirectTvRef = useRef(false);

    // Track previous DirectTV lineups to detect when new ones are added
    const prevDitvLineupsRef = useRef<Set<string>>(new Set());

    // Fetch tv_package_channels when DIRECTV package is selected (async, cached in localStorage)
    useEffect(() => {
        // Never mutate savedChannelMap from package filtering while on the list screen
        if (viewMode === 'list') {
            return;
        }

        const fetchTvPackageChannelsData = async () => {
            const ditvLineups = selectedLineups.filter((lineup) => lineup.name?.startsWith("DIRECT"));
            
            if (ditvLineups.length === 0) {
                setTvPackageChannels([]);
                return;
            }
            
            if (selectedTvPackage !== null && selectedTvPackage !== 7) {
                const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
                if (!workplaceIdValue) {
                    console.warn('tv_package_channels: workplaceId not found');
                    return;
                }
                
                console.log('tv_package_channels useEffect: Calling fetchChannels', {
                    workplaceId: workplaceIdValue,
                    packageId: selectedTvPackage,
                    useCache: true
                });
                
                // Use fetchChannels with cache enabled - it handles localStorage automatically
                // This will use cache if available, otherwise fetch from API and store in localStorage
                try {
                    const tvPackageChannelsData = await fetchChannels(workplaceIdValue, 2000, true);
                    console.log('tv_package_channels useEffect: Data received, setting state', tvPackageChannelsData.length);
                    setTvPackageChannels(tvPackageChannelsData);
                    
                    // Immediately filter and update channels when tvPackageChannels loads.
                    // Preserve manually selected channels via membership diff.
                    // Skip overwrite when edit/view already hydrated preference channels.
                    const alreadyHydrated = ditvLineups.every((lineup) =>
                        channelsInitializedRef.current.has(`${lineup.lineupId}-${selectedTvPackage}`)
                    );
                    if (
                        tvPackageChannelsData.length > 0 &&
                        ditvLineups.length > 0 &&
                        !isHydratingDirectTvRef.current &&
                        !alreadyHydrated
                    ) {
                        const fChannels = filterChannelNumbers(tvPackageChannelsData, selectedTvPackage, selectedTvAddons);
                        const previousFChannels = filterChannelNumbers(
                            tvPackageChannelsData,
                            prevPackageRef.current,
                            prevAddonsRef.current
                        );
                        
                        // Update channels immediately
                        setSavedChannelMap((prev) => {
                            const updated = { ...prev };
                            ditvLineups.forEach((lineup) => {
                                const matchedChannels = matchChannelsToLineup(fChannels, lineup.lineupId, lineup.name);
                                const previousMatched = matchChannelsToLineup(previousFChannels, lineup.lineupId, lineup.name);
                                updated[lineup.lineupId] = applyFilteredPackageChannels(
                                    matchedChannels,
                                    prev[lineup.lineupId] || [],
                                    previousMatched
                                );
                                channelsInitializedRef.current.add(`${lineup.lineupId}-${selectedTvPackage}`);
                            });
                            return updated;
                        });
                        
                        setFormValues((prev) => {
                            const updatedChannels = { ...prev.channels };
                            ditvLineups.forEach((lineup) => {
                                const matchedChannels = matchChannelsToLineup(fChannels, lineup.lineupId, lineup.name);
                                const previousMatched = matchChannelsToLineup(previousFChannels, lineup.lineupId, lineup.name);
                                updatedChannels[lineup.lineupId] = applyFilteredPackageChannels(
                                    matchedChannels,
                                    prev.channels[lineup.lineupId] || [],
                                    previousMatched
                                );
                            });
                            return { ...prev, channels: updatedChannels };
                        });
                        
                        // Update refs
                        prevPackageRef.current = selectedTvPackage;
                        prevAddonsRef.current = [...selectedTvAddons];
                    }
                } catch (error) {
                    console.error("Error fetching tv_package_channels:", error);
                    setTvPackageChannels([]);
                }
            } else {
                setTvPackageChannels([]);
                // Only clear package-driven selections while actively editing/creating.
                // Leaving edit/view nulls selectedTvPackage for cleanup — that must NOT
                // wipe savedChannelMap or the provider disappears from the list.
                if (
                    selectedTvPackage === null &&
                    (viewMode === 'create' || viewMode === 'edit')
                ) {
                    setSavedChannelMap((prev) => {
                        const updated = { ...prev };
                        ditvLineups.forEach((lineup) => {
                            updated[lineup.lineupId] = [];
                        });
                        return updated;
                    });
                    setFormValues((prev) => {
                        const updatedChannels = { ...prev.channels };
                        ditvLineups.forEach((lineup) => {
                            updatedChannels[lineup.lineupId] = [];
                        });
                        return { ...prev, channels: updatedChannels };
                    });
                    ditvLineups.forEach((lineup) => {
                        channelsInitializedRef.current.forEach((key) => {
                            if (key.startsWith(`${lineup.lineupId}-`)) {
                                channelsInitializedRef.current.delete(key);
                            }
                        });
                    });
                }
            }
        };
        
        // Call async function
        fetchTvPackageChannelsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTvPackage, selectedLineups, workplaceId, selectedTvAddons, viewMode]);

    useEffect(() => {
        // List view must keep API preference counts intact — skip package-driven rewrites
        if (viewMode === 'list') {
            return;
        }
        // Edit/view hydrate: preference API channels are already loaded — don't overwrite
        if (isHydratingDirectTvRef.current) {
            return;
        }

        // Use tvPackageChannels if available (has package/addon info), otherwise fallback to channels
        const channelsToUse = tvPackageChannels.length > 0 ? tvPackageChannels : channels;
        if (!channelsToUse || channelsToUse.length === 0) return;
        if (tvPackages.length === 0) return; // Wait for packages to load
        
        // Find all DirectTV lineups (can be multiple: DIRECTV Dallas, DIRECTV STREAM, etc.)
        // Check if lineup name starts with "DIRECT" to handle all DIRECTV variations
        const ditvLineups = selectedLineups.filter((lineup) => lineup.name?.startsWith("DIRECT"));
        const currentDitvLineupIds = new Set(ditvLineups.map(l => l.lineupId));
        
        // Check if new DirectTV lineups were added
        const newDitvLineupsAdded = ditvLineups.some(lineup => !prevDitvLineupsRef.current.has(lineup.lineupId));
        
        if (ditvLineups.length === 0) {
            prevPackageRef.current = null;
            prevAddonsRef.current = [];
            channelsInitializedRef.current.clear();
            prevDitvLineupsRef.current.clear();
            return;
        }
        
        // Check if package or addons actually changed
        const packageChanged = prevPackageRef.current !== selectedTvPackage;
        const addonsChanged = JSON.stringify(prevAddonsRef.current.sort()) !== JSON.stringify(selectedTvAddons.sort());
        
        // Check if channels need to be initialized for any DirectTV lineup
        // This handles the case when channels load after package is already selected
        // OR when new DirectTV lineups are added
        let needsChannelInitialization = false;
        if (selectedTvPackage !== null && selectedTvPackage !== 7) {
            // Check if any DirectTV lineup hasn't been initialized yet
            // OR if new DirectTV lineups were just added
            needsChannelInitialization = newDitvLineupsAdded || ditvLineups.some((lineup) => {
                const isInitialized = channelsInitializedRef.current.has(`${lineup.lineupId}-${selectedTvPackage}`);
                return !isInitialized;
            });
        }
        
        // Force filtering if:
        // 1. Package changed (including initial selection)
        // 2. Addons changed
        // 3. Channels need initialization (new lineups or not initialized yet)
        // 4. New DirectTV lineups were added (CRITICAL: must filter for new lineups even if package didn't change)
        const shouldFilter = selectedTvPackage !== null && 
            selectedTvPackage !== 7 && 
            (packageChanged || addonsChanged || needsChannelInitialization || newDitvLineupsAdded);
        
        // Only auto-filter channels for DirectTV when package is selected and not Custom
        if (shouldFilter) {
            // Filter channels based on package and addons - use tvPackageChannels if available
            const fChannels = filterChannelNumbers(channelsToUse, selectedTvPackage, selectedTvAddons);
            const previousFChannels = filterChannelNumbers(
                channelsToUse,
                prevPackageRef.current,
                prevAddonsRef.current
            );
            
            // Update channels for all DirectTV lineups
            setSavedChannelMap((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                
                ditvLineups.forEach((ditvLineup) => {
                    const filteredChannelsForLineup = matchChannelsToLineup(fChannels, ditvLineup.lineupId, ditvLineup.name);
                    const previousMatched = matchChannelsToLineup(previousFChannels, ditvLineup.lineupId, ditvLineup.name);
                    // Diff membership: drop only removed package/addon channels; keep manual picks
                    const nextChannels = applyFilteredPackageChannels(
                        filteredChannelsForLineup,
                        prev[ditvLineup.lineupId] || [],
                        previousMatched
                    );
                    const currentChannels = prev[ditvLineup.lineupId] || [];
                    
                    // Check if this is a new lineup that was just added
                    const isNewLineup = newDitvLineupsAdded && !prevDitvLineupsRef.current.has(ditvLineup.lineupId);
                    
                    // Always update if this is initialization (channels are empty) or if channels are different
                    // OR if this is a new lineup that was just added
                    const isInitialization = currentChannels.length === 0 && filteredChannelsForLineup.length > 0;
                    const currentSorted = [...currentChannels].sort().join(',');
                    const filteredSorted = [...nextChannels].sort().join(',');
                    const channelsDifferent = currentSorted !== filteredSorted;
                    
                    // Always update for new lineups or when channels are different
                    // Also update when addons/package changed to ensure all lineups get updated channels
                    if (isNewLineup || isInitialization || channelsDifferent || addonsChanged || packageChanged) {
                        // For new lineups, always set filtered channels to ensure count shows
                        updated[ditvLineup.lineupId] = nextChannels;
                        hasChanges = true;
                        
                        // Mark as initialized when we set channels
                        if (nextChannels.length > 0) {
                            channelsInitializedRef.current.add(`${ditvLineup.lineupId}-${selectedTvPackage}`);
                        }
                    }
                });
                
                return hasChanges ? updated : prev;
            });
            
            // Also update formValues for all DirectTV lineups
            // This is critical for count display - always update for DirectTV lineups when addons/package change
            setFormValues((prev) => {
                const updatedChannels = { ...prev.channels };
                let hasChanges = false;
                
                ditvLineups.forEach((ditvLineup) => {
                    // Use matchChannelsToLineup to correctly match channels (handles lineupId mismatch)
                    const filteredChannelsForLineup = matchChannelsToLineup(fChannels, ditvLineup.lineupId, ditvLineup.name);
                    const previousMatched = matchChannelsToLineup(previousFChannels, ditvLineup.lineupId, ditvLineup.name);
                    const nextChannels = applyFilteredPackageChannels(
                        filteredChannelsForLineup,
                        prev.channels[ditvLineup.lineupId] || [],
                        previousMatched
                    );
                    const currentChannels = prev.channels[ditvLineup.lineupId] || [];
                    
                    // Always update when package or addons change (not just when different)
                    // This ensures count updates immediately when checkbox is clicked
                    const currentSorted = [...currentChannels].sort().join(',');
                    const filteredSorted = [...nextChannels].sort().join(',');
                    const channelsDifferent = currentSorted !== filteredSorted;
                    
                    // Update if channels are different OR if addons/package changed
                    if (channelsDifferent || addonsChanged || packageChanged) {
                        updatedChannels[ditvLineup.lineupId] = nextChannels;
                        hasChanges = true;
                    }
                });
                
                return hasChanges ? { ...prev, channels: updatedChannels } : prev;
            });
            
            // Update refs
            prevPackageRef.current = selectedTvPackage;
            prevAddonsRef.current = [...selectedTvAddons];
            // Update DirectTV lineups ref after filtering
            prevDitvLineupsRef.current = currentDitvLineupIds;
        } else if (selectedTvPackage === 7 && packageChanged) {
            // Custom mode - only clear when user switches to Custom
            if (!isHydratingDirectTvRef.current) {
                setSavedChannelMap((prev) => {
                    const updated = { ...prev };
                    let hasChanges = false;
                    
                    ditvLineups.forEach((ditvLineup) => {
                        if (prev[ditvLineup.lineupId] && prev[ditvLineup.lineupId].length > 0) {
                            updated[ditvLineup.lineupId] = [];
                            hasChanges = true;
                        }
                    });
                    
                    return hasChanges ? updated : prev;
                });
                
                setFormValues((prev) => {
                    const updatedChannels = { ...prev.channels };
                    let hasChanges = false;
                    
                    ditvLineups.forEach((ditvLineup) => {
                        if (prev.channels[ditvLineup.lineupId] && prev.channels[ditvLineup.lineupId].length > 0) {
                            updatedChannels[ditvLineup.lineupId] = [];
                            hasChanges = true;
                        }
                    });
                    
                    return hasChanges ? { ...prev, channels: updatedChannels } : prev;
                });
                
                // Clear initialization flags for Custom mode
                ditvLineups.forEach((ditvLineup) => {
                    channelsInitializedRef.current.delete(`${ditvLineup.lineupId}-7`);
                });
            }
            
            prevPackageRef.current = 7;
            prevAddonsRef.current = [];
            // Update DirectTV lineups ref
            prevDitvLineupsRef.current = currentDitvLineupIds;
        } else if (
            selectedTvPackage === null &&
            packageChanged &&
            (viewMode === 'create' || viewMode === 'edit')
        ) {
            // Package deselected in create/edit — clear DIRECTV prefs for current selection only.
            // Do not run this when returning to list mode (package is nullled for cleanup).
            setSavedChannelMap((prev) => {
                const updated = { ...prev };
                ditvLineups.forEach((ditvLineup) => {
                    updated[ditvLineup.lineupId] = [];
                });
                return updated;
            });
            setFormValues((prev) => {
                const updatedChannels = { ...prev.channels };
                ditvLineups.forEach((ditvLineup) => {
                    updatedChannels[ditvLineup.lineupId] = [];
                });
                return { ...prev, channels: updatedChannels };
            });
            ditvLineups.forEach((ditvLineup) => {
                channelsInitializedRef.current.forEach((key) => {
                    if (key.startsWith(`${ditvLineup.lineupId}-`)) {
                        channelsInitializedRef.current.delete(key);
                    }
                });
            });
            prevPackageRef.current = null;
            prevAddonsRef.current = [];
            prevDitvLineupsRef.current = currentDitvLineupIds;
        } else if (!packageChanged && !addonsChanged && !needsChannelInitialization && !newDitvLineupsAdded) {
            // Package/addon didn't change and channels are initialized and no new lineups, update refs but don't modify channels
            prevPackageRef.current = selectedTvPackage;
            prevAddonsRef.current = [...selectedTvAddons];
            // Update DirectTV lineups ref
            prevDitvLineupsRef.current = currentDitvLineupIds;
        } else if (newDitvLineupsAdded && selectedTvPackage !== null && selectedTvPackage !== 7) {
            // New DirectTV lineups were added - filter channels for them even if package didn't change
            // Use tvPackageChannels if available (has package/addon info), otherwise fallback to channels
            const channelsToUse = tvPackageChannels.length > 0 ? tvPackageChannels : channels;
            const fChannels = filterChannelNumbers(channelsToUse, selectedTvPackage, selectedTvAddons);
            
            // Update channels only for new lineups
            setSavedChannelMap((prev) => {
                const updated = { ...prev };
                let hasChanges = false;
                
                ditvLineups.forEach((ditvLineup) => {
                    // Only process new lineups
                    if (!prevDitvLineupsRef.current.has(ditvLineup.lineupId)) {
                        // Use matchChannelsToLineup to correctly match channels for new DIRECTV lineups
                        const filteredChannelsForLineup = matchChannelsToLineup(fChannels, ditvLineup.lineupId, ditvLineup.name);
                        if (filteredChannelsForLineup.length > 0) {
                            updated[ditvLineup.lineupId] = filteredChannelsForLineup;
                            hasChanges = true;
                            channelsInitializedRef.current.add(`${ditvLineup.lineupId}-${selectedTvPackage}`);
                        }
                    }
                });
                
                return hasChanges ? updated : prev;
            });
            
            // Update formValues for new lineups
            setFormValues((prev) => {
                const updatedChannels = { ...prev.channels };
                let hasChanges = false;
                
                ditvLineups.forEach((ditvLineup) => {
                    // Only process new lineups
                    if (!prevDitvLineupsRef.current.has(ditvLineup.lineupId)) {
                        // Use matchChannelsToLineup to correctly match channels for new DIRECTV lineups
                        const filteredChannelsForLineup = matchChannelsToLineup(fChannels, ditvLineup.lineupId, ditvLineup.name);
                        if (filteredChannelsForLineup.length > 0) {
                            updatedChannels[ditvLineup.lineupId] = filteredChannelsForLineup;
                            hasChanges = true;
                        }
                    }
                });
                
                return hasChanges ? { ...prev, channels: updatedChannels } : prev;
            });
            
            // Update refs
            prevDitvLineupsRef.current = currentDitvLineupIds;
        }
    }, [channels, tvPackageChannels, selectedTvPackage, selectedTvAddons, selectedLineups, tvPackages.length, workplaceId, viewMode]);

    useEffect(() => {
        if (isHydratingDirectTvRef.current) {
            isHydratingDirectTvRef.current = false;
        }
    }, [selectedTvPackage, selectedLineups]);

    // Synchronize formValues.channels with selectedLineups
    // Only initialize missing lineups, don't clear existing ones
    useEffect(() => {
        setFormValues((prev) => {
            const updatedChannels: Record<string, string[]> = { ...prev.channels };
            let hasChanges = false;
            
            selectedLineups.forEach((lineup) => {
                // Only initialize if this lineup doesn't exist in channels
                if (!(lineup.lineupId in updatedChannels)) {
                    updatedChannels[lineup.lineupId] = savedChannelMap[lineup.lineupId] || [];
                    hasChanges = true;
                }
            });
            
            // Remove channels for lineups that are no longer selected
            Object.keys(updatedChannels).forEach((lineupId) => {
                if (!selectedLineups.some((lineup) => lineup.lineupId === lineupId)) {
                    delete updatedChannels[lineupId];
                    hasChanges = true;
                }
            });
            
            return hasChanges ? { channels: updatedChannels } : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLineups]);

    // Accordion is always open - no toggle needed

    const handleSelectChange = (selectedOptions: LineupDetail[] | null) => {
        if (selectedOptions && selectedOptions.length > 3) {
            setErrorMessage("You can select a maximum of 3 providers.");
        } else {
            setErrorMessage(null);
            setSelectedLineups(selectedOptions || []);
        }
    };

    // Handler functions for view mode
    const handleAddClick = async () => {
        if (!canExecute) {
            return;
        }
        setSelectedProvider(null);
        setViewMode('create');
        onViewModeChange?.('create');
        setSelectedLineups([]);
        setFormValues({ channels: {} });
        setSelectedTvPackage(null);
        setSelectedTvAddons([]);
        setErrorMessage(null);
        setChannels([]); // Clear channels - will load when lineup is selected
        setLoadedLineupId(null); // Reset loaded lineup ID
        isApiCallingRef.current = false; // Reset API calling flag
    };

    const handleEditClick = async (provider: LineupDetail) => {
        if (!canEdit) {
            return;
        }
        setSelectedProvider(provider);
        setViewMode('edit');
        onViewModeChange?.('edit');
        setSelectedLineups([provider]);
        setErrorMessage(null);
        
        let providerChannelsFromApi: string[] | null = null;

        // Load package and addon info for DirectTV providers BEFORE loading channels
        // This ensures package/addon is set when channels load, so useEffect can filter properly
        if (provider.name?.startsWith("DIRECT")) {
            // Always look up package/addon for THIS specific provider (don't preserve current value)
            // This fixes the issue when multiple DIRECTV providers are selected with different packages
            let packageId: number | null = null;
            let addonIds: number[] = [];
            
            // First, try to get package info from API (fetchPreferenceChannels) - this is the source of truth
            const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
            if (workplaceIdValue) {
                try {
                    const apiChannels = await fetchPreferenceChannels(workplaceIdValue, 2000, false); // Don't use cache, get fresh data
                    // Find the first channel entry for this lineup (all entries for same lineup have same package/addon)
                    const lineupChannel = apiChannels.find((item: any) => item.lineup === provider.lineupId) as any;
                    if (lineupChannel) {
                        const packageStr = lineupChannel.lineupPackage || lineupChannel.lineup_package || '';
                        if (packageStr && packageStr.trim() !== '') {
                            packageId = parseInt(packageStr);
                            if (!isNaN(packageId)) {
                                const addonStr = lineupChannel.lineupAddon || lineupChannel.lineup_addon || '';
                                if (addonStr && addonStr.trim() !== '') {
                                    addonIds = String(addonStr)
                                        .split(',')
                                        .map((id: string) => parseInt(id.trim()))
                                        .filter((id: number) => !isNaN(id));
                                } else {
                                    addonIds = [];
                                }
                                console.log('Edit mode: Found package from API for provider', provider.name, 'packageId:', packageId, 'addonIds:', addonIds);
                            }
                        }
                    }

                    // Derive saved channels for this provider directly from API response
                    providerChannelsFromApi = apiChannels
                        .filter((item: any) => item.lineup === provider.lineupId)
                        .map((item: any) => item.channel)
                        .filter((ch: any) => ch && String(ch).trim() !== '');
                } catch (error) {
                    console.error("Error fetching preference channels from API:", error);
                }
            }
            
            // Fallback to localStorage if API didn't return package info
            if (packageId === null) {
                const storedData = localStorage.getItem("customizedLineups");
                if (storedData) {
                    try {
                        const parsedData = JSON.parse(storedData);
                        const savedProviderData = parsedData.find((item: any) => item.lineup === provider.lineupId);
                        if (savedProviderData) {
                            const packageStr = savedProviderData.lineup_package || savedProviderData.lineupPackage || '';
                            packageId = packageStr && packageStr.trim() !== '' ? parseInt(packageStr) : null;
                            if (packageId !== null && !isNaN(packageId)) {
                                const addonStr = savedProviderData.lineup_addon || savedProviderData.lineupAddon || '';
                                if (addonStr && addonStr.trim() !== '') {
                                    addonIds = String(addonStr)
                                        .split(',')
                                        .map((id: string) => parseInt(id.trim()))
                                        .filter((id: number) => !isNaN(id));
                                } else {
                                    addonIds = [];
                                }
                                console.log('Edit mode: Found package from localStorage for provider', provider.name, 'packageId:', packageId, 'addonIds:', addonIds);
                            } else {
                                console.log('Edit mode: No valid package found in localStorage for provider', provider.name, 'packageStr:', packageStr);
                            }
                        } else {
                            console.log('Edit mode: No saved data found in localStorage for provider', provider.name, 'lineupId:', provider.lineupId);
                        }
                    } catch (error) {
                        console.error("Error parsing saved data from localStorage:", error);
                    }
                } else {
                    console.log('Edit mode: No customizedLineups data in localStorage');
                }
            }
            
            // ALWAYS set package and addons (even if null) to ensure state is correct
            // This ensures ChannelSelector receives the correct values immediately
            if (packageId === 7) {
                isHydratingDirectTvRef.current = true;
            } else if (packageId !== null) {
                // Prefer preference API channels on edit open — do not refilter/replace them
                isHydratingDirectTvRef.current = true;
            }
            setSelectedTvPackage(packageId);
            setSelectedTvAddons(addonIds);
            // Keep prev refs aligned with loaded values so filter effects do NOT treat this as a change
            prevPackageRef.current = packageId;
            prevAddonsRef.current = [...addonIds];
            if (packageId !== null && packageId !== 7) {
                channelsInitializedRef.current.add(`${provider.lineupId}-${packageId}`);
            }
        } else {
            // Reset package/addon for non-DirectTV providers
            setSelectedTvPackage(null);
            setSelectedTvAddons([]);
            prevPackageRef.current = null;
            prevAddonsRef.current = [];
        }

        // Load saved channels after package/addon lookup to avoid initial empty count
        const providerChannels = savedChannelMap[provider.lineupId] || [];
        const finalProviderChannels = (provider.name?.startsWith("DIRECT") && providerChannelsFromApi && providerChannelsFromApi.length > 0)
            ? providerChannelsFromApi
            : providerChannels;
        // Preference API is source of truth for preselection (includes manual NFL picks)
        setSavedChannelMap((prev) => ({
            ...prev,
            [provider.lineupId]: finalProviderChannels,
        }));
        setFormValues({ channels: { [provider.lineupId]: finalProviderChannels } });
        
        // Load channels API with lineup ID for edit mode (only if not already loaded and not currently calling)
        // This happens AFTER package/addon is set, so useEffect can filter channels properly
        if (loadedLineupId !== provider.lineupId && !isApiCallingRef.current) {
            isApiCallingRef.current = true;
            setIsLoadingChannels(true);
            try {
                const channelData = await fetchGracenoteChannels(provider.lineupId, provider.name);
                setChannels(channelData);
                setLoadedLineupId(provider.lineupId);
                console.log('Edit mode: channels API called with lineup:', provider.lineupId, 'channels loaded:', channelData.length);
            } catch (error) {
                console.error("Error fetching channels:", error);
                setErrorMessage("Failed to load channels. Please try again.");
            } finally {
                setIsLoadingChannels(false);
                isApiCallingRef.current = false;
            }
        }
    };

    const handleViewClick = async (provider: LineupDetail) => {
        setSelectedProvider(provider);
        setViewMode('view');
        onViewModeChange?.('view');
        setSelectedLineups([provider]);
        let providerChannelsFromApi: string[] | null = null;
        const providerChannels = savedChannelMap[provider.lineupId] || [];
        
        // Load channels API with lineup ID for view mode (only if not already loaded and not currently calling)
        if (loadedLineupId !== provider.lineupId && !isApiCallingRef.current) {
            isApiCallingRef.current = true;
            setIsLoadingChannels(true);
            try {
                const channelData = await fetchGracenoteChannels(provider.lineupId, provider.name);
                setChannels(channelData);
                setLoadedLineupId(provider.lineupId);
                console.log('View mode: channels API called with lineup:', provider.lineupId, 'channels loaded:', channelData.length);
            } catch (error) {
                console.error("Error fetching channels:", error);
                setErrorMessage("Failed to load channels. Please try again.");
            } finally {
                setIsLoadingChannels(false);
                isApiCallingRef.current = false;
            }
        }
        
        // Load package and addon info for DirectTV providers (needed for count display)
        if (provider.name?.startsWith("DIRECT")) {
            // Always look up package/addon for THIS specific provider
            // This fixes the issue when multiple DIRECTV providers are selected with different packages
            let packageId: number | null = null;
            let addonIds: number[] = [];
            
            // First, try to get package info from API (fetchPreferenceChannels) - this is the source of truth
            const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
            if (workplaceIdValue) {
                try {
                    const apiChannels = await fetchPreferenceChannels(workplaceIdValue, 2000, false); // Don't use cache, get fresh data
                    // Find the first channel entry for this lineup (all entries for same lineup have same package/addon)
                    const lineupChannel = apiChannels.find((item: any) => item.lineup === provider.lineupId) as any;
                    if (lineupChannel) {
                        const packageStr = lineupChannel.lineupPackage || lineupChannel.lineup_package || '';
                        if (packageStr && packageStr.trim() !== '') {
                            packageId = parseInt(packageStr);
                            if (!isNaN(packageId)) {
                                const addonStr = lineupChannel.lineupAddon || lineupChannel.lineup_addon || '';
                                if (addonStr && addonStr.trim() !== '') {
                                    addonIds = String(addonStr)
                                        .split(',')
                                        .map((id: string) => parseInt(id.trim()))
                                        .filter((id: number) => !isNaN(id));
                                } else {
                                    addonIds = [];
                                }
                                console.log('View mode: Found package from API for provider', provider.name, 'packageId:', packageId, 'addonIds:', addonIds);
                            }
                        }
                    }

                    // Derive saved channels for this provider directly from API response
                    providerChannelsFromApi = apiChannels
                        .filter((item: any) => item.lineup === provider.lineupId)
                        .map((item: any) => item.channel)
                        .filter((ch: any) => ch && String(ch).trim() !== '');
                } catch (error) {
                    console.error("Error fetching preference channels from API:", error);
                }
            }
            
            // Fallback to localStorage if API didn't return package info
            if (packageId === null) {
                const storedData = localStorage.getItem("customizedLineups");
                if (storedData) {
                    try {
                        const parsedData = JSON.parse(storedData);
                        const savedProviderData = parsedData.find((item: any) => item.lineup === provider.lineupId);
                        if (savedProviderData) {
                            const packageStr = savedProviderData.lineup_package || savedProviderData.lineupPackage || '';
                            packageId = packageStr && packageStr.trim() !== '' ? parseInt(packageStr) : null;
                            if (packageId !== null && !isNaN(packageId)) {
                                const addonStr = savedProviderData.lineup_addon || savedProviderData.lineupAddon || '';
                                if (addonStr && addonStr.trim() !== '') {
                                    addonIds = String(addonStr)
                                        .split(',')
                                        .map((id: string) => parseInt(id.trim()))
                                        .filter((id: number) => !isNaN(id));
                                } else {
                                    addonIds = [];
                                }
                                console.log('View mode: Found package from localStorage for provider', provider.name, 'packageId:', packageId, 'addonIds:', addonIds);
                            } else {
                                console.log('View mode: No valid package found in localStorage for provider', provider.name, 'packageStr:', packageStr);
                            }
                        } else {
                            console.log('View mode: No saved data found in localStorage for provider', provider.name, 'lineupId:', provider.lineupId);
                        }
                    } catch (error) {
                        console.error("Error parsing saved data from localStorage:", error);
                    }
                } else {
                    console.log('View mode: No customizedLineups data in localStorage');
                }
            }
            
            // ALWAYS set package and addons (even if null) to ensure state is correct
            // This ensures ChannelSelector receives the correct values immediately
            isHydratingDirectTvRef.current = true;
            setSelectedTvPackage(packageId);
            setSelectedTvAddons(addonIds);
            prevPackageRef.current = packageId;
            prevAddonsRef.current = [...addonIds];
            if (packageId !== null && packageId !== 7) {
                channelsInitializedRef.current.add(`${provider.lineupId}-${packageId}`);
            }
            
            // Filter channels based on package/addon using tv_package_channels API logic
            // This ensures count shows correctly
            if (packageId !== null && !isNaN(packageId)) {
                // Use tvPackageChannels if available (has package/addon info), otherwise fallback to channels
                const channelsToUseForView = tvPackageChannels.length > 0 ? tvPackageChannels : channels;
                if (channelsToUseForView && channelsToUseForView.length > 0 && packageId !== 7) {
                    // Keep preference API channels as the selection; only use filter for count padding if needed
                    const preferenceChannels = (providerChannelsFromApi && providerChannelsFromApi.length > 0)
                        ? providerChannelsFromApi
                        : providerChannels;
                    setSavedChannelMap((prev) => ({
                        ...prev,
                        [provider.lineupId]: preferenceChannels
                    }));
                    setFormValues({ channels: { [provider.lineupId]: preferenceChannels } });
                } else if (providerChannels.length > 0) {
                    // If we have saved channels, use them (for Custom mode or already saved channels)
                    setFormValues({ channels: { [provider.lineupId]: providerChannels } });
                }
            }
        } else {
            // Reset package/addon for non-DirectTV providers
            setSelectedTvPackage(null);
            setSelectedTvAddons([]);
        }

        const finalProviderChannels = (provider.name?.startsWith("DIRECT") && providerChannelsFromApi && providerChannelsFromApi.length > 0)
            ? providerChannelsFromApi
            : providerChannels;
        setFormValues({ channels: { [provider.lineupId]: finalProviderChannels } });
    };

    const restoreListSelectionFromChannelMap = (
        channelMap: Record<string, string[]>,
        packageMap?: Record<string, number | null>
    ) => {
        // List mode does not need edit selections; keep selectedLineups empty so
        // package-filter effects cannot rewrite other providers' channel counts.
        setSelectedLineups([]);
        setFormValues({ channels: { ...channelMap } });
        setSelectedTvPackage(null);
        setSelectedTvAddons([]);
        prevPackageRef.current = null;
        prevAddonsRef.current = [];
        if (packageMap) {
            setSavedPackageMap(packageMap);
        }
    };

    const buildChannelAndPackageMaps = (saved: any[]) => {
        const grouped: Record<string, string[]> = saved.reduce((acc: Record<string, string[]>, item: any) => {
            const key = item.lineup;
            if (!acc[key]) acc[key] = [];
            if (item.channel && String(item.channel).trim() !== '') {
                acc[key].push(item.channel);
            }
            return acc;
        }, {});

        const packageGrouped: Record<string, number | null> = saved.reduce((acc: Record<string, number | null>, item: any) => {
            const key = item.lineup;
            const packageStr = item.lineupPackage || item.lineup_package || '';
            if (!(key in acc)) {
                acc[key] = packageStr && String(packageStr).trim() !== '' ? parseInt(packageStr) : null;
                if (acc[key] !== null && Number.isNaN(acc[key] as number)) {
                    acc[key] = null;
                }
            }
            return acc;
        }, {});

        return { grouped, packageGrouped };
    };

    const handleBackClick = async () => {
        setViewMode('list');
        onViewModeChange?.('list');
        setSelectedProvider(null);
        setErrorMessage(null);
        setSelectedLineups([]);
        setSelectedTvPackage(null);
        setSelectedTvAddons([]);
        prevPackageRef.current = null;
        prevAddonsRef.current = [];

        // Restore list counts strictly from preference API (no package-filter placeholders)
        const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
        if (workplaceIdValue) {
            try {
                const saved: any[] = await fetchPreferenceChannels(workplaceIdValue, 2000, false);
                const { grouped, packageGrouped } = buildChannelAndPackageMaps(saved);
                setSavedChannelMap(grouped);
                restoreListSelectionFromChannelMap(grouped, packageGrouped);
            } catch (error) {
                console.error("Error refreshing providers on back:", error);
                restoreListSelectionFromChannelMap(savedChannelMap);
            }
        } else {
            restoreListSelectionFromChannelMap(savedChannelMap);
        }
    };

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [providerToDelete, setProviderToDelete] = useState<LineupDetail | null>(null);

    const handleDeleteClick = (provider: LineupDetail) => {
        if (!canDelete) {
            return;
        }
        setProviderToDelete(provider);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!providerToDelete) return;
        
        setIsSubmitting(true);
        try {
            const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
            if (!workplaceIdValue) {
                setErrorMessage("Workplace ID not found. Please refresh the page.");
                setIsDeleteDialogOpen(false);
                setProviderToDelete(null);
                return;
            }
            
            // Call API to delete preference channels by lineup ID
            await deletePreferenceChannelsByLineup(providerToDelete.lineupId, workplaceIdValue);
            
            // Clear cache for preference channels to ensure fresh data
            clearPreferenceChannelsCache(workplaceIdValue);
            
            // Recall preference channels API to get updated response
            const saved: any[] = await fetchPreferenceChannels(workplaceIdValue, 2000, false);
            
            // Group saved channels by lineup id -> string[] of channel codes
            const grouped: Record<string, string[]> = saved.reduce((acc: Record<string, string[]>, item: any) => {
                const key = item.lineup;
                if (!acc[key]) acc[key] = [];
                if (item.channel && String(item.channel).trim() !== '') {
                    acc[key].push(item.channel);
                }
                return acc;
            }, {});
            
            // Update savedChannelMap with fresh data from API
            setSavedChannelMap(grouped);

            const packageGrouped: Record<string, number | null> = saved.reduce((acc: Record<string, number | null>, item: any) => {
                const key = item.lineup;
                const packageStr = item.lineupPackage || item.lineup_package || '';
                if (!(key in acc)) {
                    acc[key] = packageStr && String(packageStr).trim() !== '' ? parseInt(packageStr) : null;
                    if (acc[key] !== null && Number.isNaN(acc[key] as number)) {
                        acc[key] = null;
                    }
                }
                return acc;
            }, {});
            setSavedPackageMap(packageGrouped);
            
            // Update formValues with fresh data
            setFormValues({ channels: grouped });
            
            // Remove provider from selectedLineups
            const updated = selectedLineups.filter(l => l.lineupId !== providerToDelete.lineupId);
            setSelectedLineups(updated);
            
            // Clear localStorage entry for deleted provider to ensure it can be added back
            const storedData = localStorage.getItem("customizedLineups");
            if (storedData) {
                try {
                    const parsedData = JSON.parse(storedData);
                    const filteredData = parsedData.filter((item: any) => item.lineup !== providerToDelete.lineupId);
                    localStorage.setItem("customizedLineups", JSON.stringify(filteredData));
                } catch (error) {
                    console.error("Error clearing localStorage for deleted provider:", error);
                }
            }
            
            // Invalidate queries to refresh the list
            queryClient.invalidateQueries(['preferenceChannels']);
            queryClient.invalidateQueries(['gracenoteLineups']);

            // Only call activate-schedule API with 0 when deleting the last provider; otherwise no API call.
            const wasLastProvider = selectedLineups.length === 1;
            if (wasLastProvider && workplaceIdValue) {
                try {
                    await callActivateDeactivateScheduling(workplaceIdValue, 0);
                    await queryClient.invalidateQueries(['gameMasterSettings', workplaceIdValue]);
                } catch (e) {
                    console.warn('Deactivate scheduling call failed:', e);
                }
            }
            
            // Show success message
            setSnackbar({
                open: true,
                message: `Provider "${providerToDelete.name}" deleted successfully.`,
                severity: 'success',
            });
            
            setIsDeleteDialogOpen(false);
            setProviderToDelete(null);
        } catch (error: any) {
            console.error("Error deleting provider:", error);
            setErrorMessage(error?.response?.data?.message || "Failed to delete provider. Please try again.");
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Failed to delete provider. Please try again.",
                severity: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteDialogOpen(false);
        setProviderToDelete(null);
    };

    const handleSubmitForProvider = async (lineupsToSave: LineupDetail[], channelsToSave: { channels: Record<string, string[]> }) => {
        setIsSubmitting(true);
        try {
            const storedData = localStorage.getItem("customizedLineups");
            let parsedData: any[] = [];

            if (storedData) {
                try {
                    parsedData = JSON.parse(storedData);
                } catch (error) {
                    console.error("Failed to parse customizedLineups:", error);
                    parsedData = [];
                }
            }

            // Remove all existing entries for lineups we're saving
            parsedData = parsedData.filter((item: any) => 
                !lineupsToSave.some(l => l.lineupId === item.lineup)
            );
            
            // Add new entries
            lineupsToSave.forEach((lineup) => {
                const lineupChannels = channelsToSave.channels[lineup.lineupId] || [];
                let lineupPackage = "";
                let lineupAddon = "";
                
                if (lineup.name?.startsWith("DIRECT")) {
                    if (selectedTvPackage === 7) {
                        lineupPackage = "7";
                    } else if (selectedTvPackage) {
                        lineupPackage = selectedTvPackage.toString();
                    }
                    
                    if (selectedTvAddons.length > 0) {
                        lineupAddon = selectedTvAddons.join(",");
                    }
                }
                
                const additionalLookup = getAdditionalChannelLookup();
                const gracenoteLookup = (channels || []).reduce(
                    (acc: Record<string, { callSign?: string; stationId?: string }>, ch: any) => {
                        const key = String(ch?.channel ?? "").trim();
                        if (key && !acc[key]) {
                            acc[key] = { callSign: ch.callSign, stationId: ch.stationId };
                        }
                        return acc;
                    },
                    {}
                );

                const channelObjects = lineupChannels.length > 0 && lineupChannels.some((ch) => ch.trim() !== "")
                    ? lineupChannels
                        .filter((channel) => channel.trim() !== "" && !String(channel).startsWith("_count_"))
                        .map((channel) => {
                            const meta = additionalLookup[channel] || gracenoteLookup[channel];
                            return {
                                channel,
                                call_sign: meta?.callSign ?? null,
                                station_id: meta?.stationId ?? null,
                            };
                        })
                    : [{ channel: "", call_sign: null, station_id: null }];

                parsedData.push({
                    lineup: lineup.lineupId,
                    lineup_package: lineupPackage,
                    lineup_addon: lineupAddon,
                    channels: channelObjects,
                });
            });

            // Store formatted payload
            localStorage.setItem("formattedPayload", JSON.stringify(parsedData));
            localStorage.setItem("customizedLineups", JSON.stringify(parsedData));

            // Call mutation with the payload
            await channelsMutation.mutateAsync(parsedData);

            // Clear cache and refetch fresh data after successful save
            const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
            clearPreferenceChannelsCache(workplaceIdValue);
            clearTvPackageChannelsCache(workplaceIdValue);

            // Refresh data
            const saved: any[] = await fetchPreferenceChannels(workplaceIdValue, 2000, false);
            const { grouped, packageGrouped } = buildChannelAndPackageMaps(saved);
            setSavedChannelMap(grouped);
            setSavedPackageMap(packageGrouped);
            
            // Update formValues with fresh data
            setFormValues((prev) => ({
                ...prev,
                channels: grouped,
            }));
            
            setSnackbar({
                open: true,
                message: 'Channels saved successfully',
                severity: 'success',
            });

            try {
                await checkAndAutoActivateScheduling(workplaceIdValue);
            } catch (e) {
                console.warn('Auto-activate scheduling check failed:', e);
            }
            await queryClient.invalidateQueries(['gameMasterSettings', workplaceIdValue]);
            
            if (viewMode === 'create' || viewMode === 'edit') {
                setViewMode('list');
                onViewModeChange?.('list');
                setSelectedProvider(null);
                restoreListSelectionFromChannelMap(grouped, packageGrouped);
            }
        } catch (error: any) {
            console.error("Error submitting channels:", error);
            setErrorMessage("Failed to submit channels.");
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || 'Failed to save channels. Please try again.',
                severity: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper function to get addon IDs for a package
    const getAddonsForPackage = useCallback((pkg: TvPackage, allAddons: TvAddon[]): number[] => {
        const addonIds: number[] = [];
        
        // Check addons array in package
        if (pkg.addons && Array.isArray(pkg.addons)) {
            pkg.addons.forEach((addon: any) => {
                if (typeof addon === 'string') {
                    // If it's a string (URL), extract ID
                    const match = addon.match(/\/(\d+)$/);
                    if (match) {
                        addonIds.push(Number(match[1]));
                    }
                } else if (addon && typeof addon.id === 'number') {
                    addonIds.push(addon.id);
                }
            });
        }

        // Also check addons that have this package in their tvPackages array
        allAddons.forEach((addon) => {
            if (addon.tvPackages && Array.isArray(addon.tvPackages)) {
                const hasPackage = addon.tvPackages.some((pkgRef: any) => {
                    if (typeof pkgRef === 'string') {
                        return pkgRef.includes(`/tv_packages/${pkg.id}`);
                    } else if (pkgRef && typeof pkgRef.id === 'number') {
                        return pkgRef.id === pkg.id;
                    }
                    return false;
                });
                
                if (hasPackage && !addonIds.includes(addon.id)) {
                    addonIds.push(addon.id);
                }
            }
        });

        return addonIds;
    }, []);

    // Fetch TV packages and addons when DirectTV lineups are selected
    // Check if any selected lineup is DirectTV
    const hasDirectTvLineup = selectedLineups.some(
        (lineup) => lineup.name && lineup.name.startsWith("DIRECT")
    );

    // Fetch TV packages and addons in create/edit/view modes, not in list mode
    // View mode needs TV data for count display
    const shouldFetchTvData = (viewMode === 'create' || viewMode === 'edit' || viewMode === 'view') && hasDirectTvLineup;

    // Use useQuery for TV packages
    const { data: tvPackagesData = [], isLoading: isLoadingPackages } = useQuery(
        ['tvPackages'],
        () => fetchTvPackages(true), // Wrap to match useQuery signature
        {
            enabled: shouldFetchTvData,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 10 * 60 * 1000, // 10 minutes - TV packages don't change often
        }
    );

    // Use useQuery for TV addons
    const { data: tvAddonsData = [], isLoading: isLoadingAddons } = useQuery(
        ['tvAddons'],
        () => fetchTvAddons(true), // Wrap to match useQuery signature
        {
            enabled: shouldFetchTvData,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 10 * 60 * 1000, // 10 minutes - TV addons don't change often
        }
    );

    // Sync useQuery data to state
    useEffect(() => {
        if (shouldFetchTvData && tvPackagesData.length > 0) {
            setTvPackages(tvPackagesData);
        }
    }, [tvPackagesData, shouldFetchTvData]);

    useEffect(() => {
        if (shouldFetchTvData && tvAddonsData.length > 0) {
            setTvAddons(tvAddonsData);
        }
    }, [tvAddonsData, shouldFetchTvData]);

    // Drop hidden addons (e.g. "Test Channel (receiver systems)") from any restored selection
    useEffect(() => {
        if (tvAddons.length === 0 || selectedTvAddons.length === 0) return;
        const visibleIds = new Set(tvAddons.map((addon) => addon.id));
        const pruned = selectedTvAddons.filter((id) => visibleIds.has(id));
        if (pruned.length !== selectedTvAddons.length) {
            setSelectedTvAddons(pruned);
            prevAddonsRef.current = [...pruned];
        }
    }, [tvAddons, selectedTvAddons]);

    useEffect(() => {
        setIsLoadingTvPackages(isLoadingPackages || isLoadingAddons);
    }, [isLoadingPackages, isLoadingAddons]);

    // Clear TV data if not in create/edit mode or no DirectTV lineup is selected
    useEffect(() => {
        if (!shouldFetchTvData) {
            setTvPackages([]);
            setTvAddons([]);
            setSelectedTvPackage(null);
            setSelectedTvAddons([]);
        }
    }, [shouldFetchTvData]);

    // Handle TV package change (radio button)
    const handleTvPackageChange = (packageId: number) => {
        if (selectedTvPackage === packageId) return; // Already selected

        const newPackage = tvPackages.find(pkg => pkg.id === packageId);
        if (!newPackage) return;
        
        // Find all DIRECTV lineups (can be multiple: DIRECTV, DIRECTV STREAM, etc.)
        const ditvLineups = selectedLineups.filter((lineup) => lineup.name?.startsWith("DIRECT"));
        
        if (packageId === 7) {
            // Custom mode
            // Save current package to ref before changing
            prevPackageRef.current = selectedTvPackage;
            prevAddonsRef.current = [...selectedTvAddons];
            
            setSelectedTvPackage(7);
            setSelectedPackageObj(newPackage);
            setSelectedTvAddons([]);
            
            // Clear channels for ALL DirectTV lineups - user will select manually
            if (ditvLineups.length > 0) {
                setSavedChannelMap((prev) => {
                    const updated = { ...prev };
                    ditvLineups.forEach((lineup) => {
                        updated[lineup.lineupId] = [];
                    });
                    return updated;
                });
                setFormValues((prev) => {
                    const updatedChannels = { ...prev.channels };
                    ditvLineups.forEach((lineup) => {
                        updatedChannels[lineup.lineupId] = [];
                    });
                    return {
                        ...prev,
                        channels: updatedChannels,
                    };
                });
                
                // Clear initialization flags for Custom mode
                ditvLineups.forEach((lineup) => {
                    channelsInitializedRef.current.delete(`${lineup.lineupId}-7`);
                });
            }
            
            // Update refs immediately
            prevPackageRef.current = 7;
            prevAddonsRef.current = [];
        } else {
            // Predefined package (1-6)
            // Reset prevPackageRef to trigger useEffect change detection
            prevPackageRef.current = selectedTvPackage;
            prevAddonsRef.current = [...selectedTvAddons];
            
            setSelectedTvPackage(packageId);
            setSelectedPackageObj(newPackage);
            
            // Find compatible addons (addons that work with this package)
                const matchedAddonIds = tvAddons
                .filter((addon) => {
                    if (!addon.tvPackages) return false;
                    return addon.tvPackages.some((pkgRef: any) => {
                        if (typeof pkgRef === 'string') {
                            return pkgRef.includes(`/tv_packages/${newPackage.id}`);
                        } else if (pkgRef && typeof pkgRef.id === 'number') {
                            return pkgRef.id === newPackage.id;
                        }
                        return false;
                    });
                })
                .map((addon) => addon.id);
            
            setSelectedTvAddons(matchedAddonIds);
            
            // Filter channels: package channels + addon channels
            // Handle all DirectTV lineups (DIRECTV Dallas, DIRECTV STREAM, etc.)
            const allDitvLineups = selectedLineups.filter((lineup) => lineup.name?.startsWith("DIRECT"));
            
            // Helper function to filter and update channels
            const filterAndUpdateChannels = (channelsToUse: any[]) => {
                if (channelsToUse.length > 0 && allDitvLineups.length > 0) {
                    // Use filterChannelNumbers to properly group by lineupId
                    const fChannels = filterChannelNumbers(channelsToUse, packageId, matchedAddonIds);
                    
                    // Update all DirectTV lineups immediately
                    // Match channels to lineups - channels might have different lineupId format (e.g., USA-DITV623-X vs USA-DITV-X)
                    setSavedChannelMap((prev) => {
                        const updated = { ...prev };
                        allDitvLineups.forEach((lineup) => {
                            const matchedChannels = matchChannelsToLineup(fChannels, lineup.lineupId, lineup.name);
                            // For DIRECTV package selection, always start from a clean filtered list
                            // to keep the package count stable (e.g. 80) and avoid inflating it
                            // by merging with any previously saved channels.
                            updated[lineup.lineupId] = matchedChannels;
                            // Mark as initialized
                            channelsInitializedRef.current.add(`${lineup.lineupId}-${packageId}`);
                        });
                        return updated;
                    });
                    
                    setFormValues((prev) => {
                        const updatedChannels = { ...prev.channels };
                        allDitvLineups.forEach((lineup) => {
                            // Use matchChannelsToLineup to correctly match channels for all DIRECTV lineups
                            const matchedChannels = matchChannelsToLineup(fChannels, lineup.lineupId, lineup.name);
                            // Same reasoning as above: don't merge old channels here, use the
                            // filtered package list as the single source of truth.
                            updatedChannels[lineup.lineupId] = matchedChannels;
                        });
                        return {
                            ...prev,
                            channels: updatedChannels,
                        };
                    });
                    
                    // Update refs immediately after filtering
                    prevPackageRef.current = packageId;
                    prevAddonsRef.current = [...matchedAddonIds];
                }
            };
            
            // Filter channels immediately ONLY when tvPackageChannels are available.
            // This avoids using the generic "channels" dataset, which can produce
            // an incorrect first count (e.g., 85 instead of 88) before package data loads.
            if (tvPackageChannels.length > 0) {
                filterAndUpdateChannels(tvPackageChannels);
            } else {
                // tvPackageChannels not loaded yet - mark for initialization so useEffect
                // (which runs when tvPackageChannels arrive) will filter when data is ready.
                allDitvLineups.forEach((lineup) => {
                    channelsInitializedRef.current.delete(`${lineup.lineupId}-${packageId}`);
                });
                // Update refs so useEffect knows package changed and will filter when channels load
                prevPackageRef.current = packageId;
                prevAddonsRef.current = [...matchedAddonIds];
            }
            
            // Call tv_package_channels API asynchronously when package is selected (cached in localStorage)
            // After loading, filter channels again to ensure they're updated
            const workplaceIdValue = workplaceId || getCurrentWorkplaceId();
            if (workplaceIdValue) {
                // Use fetchChannels with cache enabled - it handles localStorage automatically
                // This will use cache if available, otherwise fetch from API and store in localStorage
                fetchChannels(workplaceIdValue, 2000, true)
                    .then((tvPackageChannelsData) => {
                        setTvPackageChannels(tvPackageChannelsData);
                        // Filter channels again after tvPackageChannels loads
                        if (tvPackageChannelsData.length > 0 && allDitvLineups.length > 0) {
                            filterAndUpdateChannels(tvPackageChannelsData);
                        }
                    })
                    .catch((error) => {
                        console.error("Error fetching tv_package_channels from handleTvPackageChange:", error);
                        setTvPackageChannels([]);
                    });
            }
        }
    };

    const channelsMutation = useChannelsMutation();
    const { isLoading } = useChannelsMutation(); // Loading state for submission

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        // Use the same handleSubmitForProvider function to maintain consistency
        await handleSubmitForProvider(selectedLineups, formValues);
    };

    const handleRebuildScheduleClick = () => {
        if (!canExecute) {
            return;
        }
        setIsRebuildDialogOpen(true);
    };

    const handleRebuildSchedule = async () => {
        try {
            setIsRebuilding(true);
            setIsRebuildDialogOpen(false);

            await RebuildSchedule();

            // Invalidate and refetch game master data queries
            await queryClient.invalidateQueries(['gameMaster', workplaceId]);
            await queryClient.refetchQueries(['gameMaster', workplaceId]);
            
            setSnackbar({
                open: true,
                message: 'Schedule rebuilt successfully',
                severity: 'success',
            });
        } catch (error) {
            console.error("Error rebuilding schedule:", error);
            setSnackbar({
                open: true,
                message: 'Failed to rebuild schedule. Please try again.',
                severity: 'error',
            });
        } finally {
            setIsRebuilding(false);
        }
    };

    // Get providers that have saved channels (selected providers)
    // Only use savedChannelMap from API - all data should be dynamic from API, not localStorage
    const savedProviders = useMemo(() => {
        const providers: LineupDetail[] = [];
        
        // Only check savedChannelMap from API - all data should be dynamic from API
        lineup.forEach((l: LineupDetail) => {
            const hasChannels = savedChannelMap[l.lineupId] && savedChannelMap[l.lineupId].length > 0;
            if (hasChannels) {
                providers.push(l);
            }
        });
        
        return providers;
    }, [lineup, savedChannelMap]);

    // Available providers for selection (exclude already selected ones in edit mode and in create mode)
    const availableProvidersForEdit = viewMode === 'edit' && selectedProvider
        ? lineup.filter((l: LineupDetail) => 
            l.lineupId === selectedProvider.lineupId || 
            !savedProviders.some(sp => sp.lineupId === l.lineupId)
          )
        : lineup.filter((option: LineupDetail) => 
            option.name !== "DIRECTV ESPN+ Business" && 
            !savedProviders.some(sp => sp.lineupId === option.lineupId)
          );

    const hasSelectedProviderWithChannels = savedProviders.length > 0;

    const handleRebuildScheduleButtonClick = () => {
        if (!canExecute) {
            setSnackbar({
                open: true,
                message: 'You do not have permission to rebuild the schedule.',
                severity: 'error',
            });
            return;
        }

        if (isRebuilding) {
            return;
        }

        if (!hasSelectedProviderWithChannels) {
            setSnackbar({
                open: true,
                message: 'Please select at least one provider or channel before rebuilding the schedule.',
                severity: 'error',
            });
            return;
        }

        handleRebuildScheduleClick();
    };

    return (
        <div style={{ width: '100%', margin: '10px 0', fontFamily: 'var(--Gotham)', display: 'block' }}>
            {/* Header bar (no toggle now, accordion always open by default) */}
            {viewMode === 'list' && ( 
                <div className={styles.accordionButton} style={{ display: 'flex' }}>
                    <h5 style={{ margin: 0, fontSize: '1.3rem', color: '#333', fontWeight: 500 }}></h5>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', pointerEvents: 'auto' }}>
                        {/* Scheduling status commented out for now */}
                        <span
                            style={{
                                marginRight: '10px',
                                fontWeight: 500,
                                color: activateScheduling ? '#446388' : '#d72638',
                            }}
                        >
                            {activateScheduling ? 'Scheduling Active' : 'Scheduling Not Active'}
                        </span>
                        
                        <button
                            onClick={handleRebuildScheduleButtonClick}
                            className={styles.rebuildButton}
                            disabled={isRebuilding}
                            style={{
                                cursor: (!canExecute || !hasSelectedProviderWithChannels || isRebuilding) ? 'not-allowed' : 'pointer',
                                opacity: (!canExecute || !hasSelectedProviderWithChannels || isRebuilding) ? 0.6 : 1,
                            }}
                            title={
                                !canExecute
                                    ? 'You do not have permission to rebuild'
                                    : !hasSelectedProviderWithChannels
                                        ? 'Select at least one provider or channel to rebuild'
                                        : 'Rebuild Schedule'
                            }
                        >
                            {isRebuilding ? 'Rebuilding...' : 'Rebuild Schedule'}
                        </button>
                    </div>
                </div>
            )}
            {/* Accordion content always visible - open by default */}
            <div className={styles.accordionContent}>
                <>
                    {viewMode === 'list' ? (
                        <div className={styles.deviceCard}>
                            <div className={styles.header}>
                                <h2 className={styles.formTitle}>Provider & Channels</h2>
                                <button
                                    className={styles.addButton}
                                    onClick={handleAddClick}
                                    disabled={!canExecute || savedProviders.length >= 3}
                                    title={
                                        !canExecute
                                            ? "You do not have permission to add providers"
                                            : savedProviders.length >= 3
                                                ? "Maximum 3 providers allowed"
                                                : "Add new provider"
                                    }
                                    style={{
                                        backgroundColor: !canExecute || savedProviders.length >= 3 ? 'gray' : 'rgb(68, 99, 136)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '5px 15px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        textTransform: 'none',
                                        cursor: !canExecute || savedProviders.length >= 3 ? 'not-allowed' : 'pointer',
                                        opacity: !canExecute || savedProviders.length >= 3 ? 0.6 : 1
                                    }}
                                >
                                    Add
                                </button>
                            </div>

                            {/* Show CircularProgress in content area while initial data is being fetched or during submission */}
                            {(isInitialLoading || isLoading) ? (
                                <div style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: 'gray',
                                    padding: '40px 20px',
                                    minHeight: '200px'
                                }}>
                                    <CircularProgress size={25} />
                                </div>
                            ) : (
                                <>
                                    {/* Show loading indicator when channels are being loaded in list view */}
                                    {isLoadingChannels && viewMode === 'list' && (
                                <div style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '40px 20px',
                                    color: 'gray'
                                }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <CircularProgress size={40} style={{ marginBottom: '10px' }} />
                                        <div>Loading channels...</div>
                                    </div>
                                </div>
                            )}
                            {savedProviders.length > 0 ? (
                                <div className={styles.tableContainer}>
                                    <table className={styles.providerTable}>
                                        <thead>
                                            <tr>
                                                <th>Provider Name</th>
                                                <th>Channels</th>
                                                <th style={{ padding: '10px', width: '48px', textAlign: 'center' }}>View</th>
                                                <th style={{ padding: '10px', width: '48px', textAlign: 'center' }}>Edit</th>
                                                <th style={{ padding: '10px', width: '48px', textAlign: 'center' }}>Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {savedProviders.map((provider: LineupDetail) => {
                                                // List counts always come from saved preference channels (API),
                                                // never from edit-time package-filter placeholders (_count_*).
                                                const providerChannels = savedChannelMap[provider.lineupId] || [];
                                                const channelCount = providerChannels.filter((ch: string) =>
                                                    ch &&
                                                    String(ch).trim() !== "" &&
                                                    !String(ch).startsWith("_count_")
                                                ).length;
                                                
                                                return (
                                                    <tr key={provider.lineupId}>
                                                        <td>{provider.name}</td>
                                                        <td>{channelCount}</td>
                                                        <td>
                                                            <button
                                                                className={styles.iconButton}
                                                                onClick={() => handleViewClick(provider)}
                                                                title="View Provider"
                                                            >
                                                                <Visibility className={styles.editIcon} />
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className={styles.iconButton}
                                                                onClick={() => handleEditClick(provider)}
                                                                title={canEdit ? "Edit Provider" : "You do not have permission to edit"}
                                                                disabled={!canEdit}
                                                                style={{
                                                                    opacity: canEdit ? 1 : 0.5,
                                                                    cursor: canEdit ? 'pointer' : 'not-allowed'
                                                                }}
                                                            >
                                                                <Edit className={styles.editIcon} />
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className={styles.iconButton}
                                                                onClick={() => handleDeleteClick(provider)}
                                                                title={canDelete ? "Delete Provider" : "You do not have permission to delete"}
                                                                disabled={!canDelete}
                                                                style={{
                                                                    opacity: canDelete ? 1 : 0.5,
                                                                    cursor: canDelete ? 'pointer' : 'not-allowed'
                                                                }}
                                                            >
                                                                <Delete className={styles.deleteIcon} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className={styles.noDataMessage}>
                                    <table className={styles.providerTable}>
                                        <thead>
                                            <tr>
                                                <th>Provider Name</th>
                                                <th>Channels</th>
                                                <th style={{ padding: '10px', width: '48px', textAlign: 'center' }}>View</th>
                                                <th style={{ padding: '10px', width: '48px', textAlign: 'center' }}>Edit</th>
                                                <th style={{ padding: '10px', width: '48px', textAlign: 'center' }}>Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                                    No records found
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div
                            className={styles.deviceCard}
                            style={{
                                minHeight: viewMode === 'create' || viewMode === 'edit' ? '600px' : 'auto',
                                // Reduce top padding so Back button is closer to top, like Screen Group
                                padding: '10px 20px 20px 20px',
                            }}
                        >
                            {/* Header with Back / Save buttons and title, similar to Screen Group layout */}
                            <div className={styles.editModeButtons}>
                                <div>
                                    <button
                                        className={styles.backButton}
                                        onClick={handleBackClick}
                                        style={{
                                            backgroundColor: 'rgb(68, 99, 136)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '5px 15px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            textTransform: 'none',
                                            minWidth: '67px',
                                        }}
                                    >
                                        Back
                                    </button>
                                </div>
                                {viewMode !== 'view' && (() => {
                                    // Check if any channels are selected
                                    const hasSelectedChannels = selectedLineups.some((lineup) => {
                                        const lineupChannels = formValues.channels[lineup.lineupId] || [];
                                        // Filter out empty strings and check if any channels exist
                                        return lineupChannels.filter((ch: string) => ch && String(ch).trim() !== '').length > 0;
                                    });
                                    
                                    const canSave = viewMode === 'create' ? canExecute : canEdit;
                                    const isSaveDisabled = isSubmitting || !canSave || selectedLineups.length === 0 || !hasSelectedChannels;
                                    
                                    return (
                                        <div style={{ marginLeft: 'auto' }}>
                                            <button
                                                onClick={handleSubmit}
                                                disabled={isSaveDisabled}
                                                style={{
                                                    backgroundColor: isSaveDisabled ? 'gray' : 'rgb(68, 99, 136)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '5px 15px',
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                    cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                                                    textTransform: 'none',
                                                    minWidth: '67px',
                                                }}
                                                title={canSave ? "Save" : "You do not have permission to save"}
                                            >
                                                {isSubmitting ? 'Saving...' : 'Save'}
                                            </button>
                                        </div>
                                    );
                                })()}
                            </div>

                            <h2 className={styles.formTitle}>
                                {viewMode === 'create'
                                    ? 'Create Provider & Channels'
                                    : viewMode === 'edit'
                                    ? 'Edit Provider & Channels'
                                    : 'View Provider & Channels'}
                            </h2>

                            {/* Provider Selector (Top) */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                    Select Provider {viewMode === 'edit' ? '' : ''}
                                </label>
                                <Select
                                    isDisabled={viewMode === 'edit' || viewMode === 'view'}
                                    isMulti={false}
                                    options={availableProvidersForEdit}
                                    value={selectedLineups.length > 0 ? selectedLineups[0] : null}
                                    // @ts-ignore
                                    onChange={async (selected: LineupDetail | null) => {
                                        if (selected) {
                                            setSelectedLineups([selected]);
                                            setErrorMessage(null);
                                            
                                            // Load channels API with lineup ID when provider is selected in add mode (only if not already loaded and not currently calling)
                                            if (loadedLineupId !== selected.lineupId && !isApiCallingRef.current) {
                                                isApiCallingRef.current = true;
                                                setIsLoadingChannels(true);
                                                try {
                                                    const channelData = await fetchGracenoteChannels(selected.lineupId, selected.name);
                                                    setChannels(channelData);
                                                    setLoadedLineupId(selected.lineupId);
                                                    console.log('Add mode: channels API called with lineup:', selected.lineupId, 'channels loaded:', channelData.length);
                                                } catch (error) {
                                                    console.error("Error fetching channels:", error);
                                                    setErrorMessage("Failed to load channels. Please try again.");
                                                } finally {
                                                    setIsLoadingChannels(false);
                                                    isApiCallingRef.current = false;
                                                }
                                            }
                                            
                                            // Load saved channels first - preserve user's selected channels
                                            const providerChannels = savedChannelMap[selected.lineupId] || [];
                                            setFormValues({ channels: { [selected.lineupId]: providerChannels } });
                                            
                                            // Load package and addon info for DirectTV providers
                                            // In add mode, don't pre-fill anything - let user select package first
                                            if (selected.name?.startsWith("DIRECT")) {
                                                if (viewMode === 'create') {
                                                    // Add mode: Don't pre-fill package/addon, let user select
                                                    setSelectedTvPackage(null);
                                                    setSelectedTvAddons([]);
                                                    prevPackageRef.current = null;
                                                    prevAddonsRef.current = [];
                                                    // Clear channels - user will select package and then channels will be filtered
                                                    setFormValues({ channels: { [selected.lineupId]: [] } });
                                                } else {
                                                    // Edit/View mode: Load package/addon from saved data
                                                    const storedData = localStorage.getItem("customizedLineups");
                                                    if (storedData) {
                                                        try {
                                                            const parsedData = JSON.parse(storedData);
                                                            const savedProviderData = parsedData.find((item: any) => item.lineup === selected.lineupId);
                                                            if (savedProviderData) {
                                                                const packageStr = savedProviderData.lineup_package || savedProviderData.lineupPackage || '';
                                                                const packageId = packageStr ? parseInt(packageStr) : null;
                                                                if (packageId && !isNaN(packageId)) {
                                                                    // Set package first
                                                                    setSelectedTvPackage(packageId);
                                                                    prevPackageRef.current = null; // Reset to trigger package change detection
                                                                    
                                                                    const addonStr = savedProviderData.lineup_addon || savedProviderData.lineupAddon || '';
                                                                    if (addonStr && addonStr.trim() !== '') {
                                                                        const addonIds = String(addonStr)
                                                                            .split(',')
                                                                            .map((id: string) => parseInt(id.trim()))
                                                                            .filter((id: number) => !isNaN(id));
                                                                        setSelectedTvAddons(addonIds);
                                                                        prevAddonsRef.current = [];
                                                                    } else {
                                                                        setSelectedTvAddons([]);
                                                                        prevAddonsRef.current = [];
                                                                    }
                                                                    
                                                                    // If we have saved channels, preserve them - don't filter
                                                                    // Only filter if there are no saved channels (new provider or channels were cleared)
                                                                    // Use tvPackageChannels if available (has package/addon info), otherwise fallback to channels
                                                                    const channelsToUseForAdd = tvPackageChannels.length > 0 ? tvPackageChannels : channels;
                                                                    if (providerChannels.length === 0 && channelsToUseForAdd.length > 0 && packageId !== 7) {
                                                                        // No saved channels - filter based on package
                                                                        const fChannels = filterChannelNumbers(channelsToUseForAdd, packageId, prevAddonsRef.current);
                                                                        const filteredChannelsForLineup = matchChannelsToLineup(fChannels, selected.lineupId, selected.name);
                                                                        setFormValues({ channels: { [selected.lineupId]: filteredChannelsForLineup } });
                                                                        channelsInitializedRef.current.add(`${selected.lineupId}-${packageId}`);
                                                                        prevPackageRef.current = packageId;
                                                                        prevAddonsRef.current = addonStr && addonStr.trim() !== '' 
                                                                            ? String(addonStr).split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id))
                                                                            : [];
                                                                    } else if (providerChannels.length > 0) {
                                                                        // We have saved channels - preserve them and mark as initialized
                                                                        channelsInitializedRef.current.add(`${selected.lineupId}-${packageId}`);
                                                                        prevPackageRef.current = packageId;
                                                                        prevAddonsRef.current = addonStr && addonStr.trim() !== '' 
                                                                            ? String(addonStr).split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id))
                                                                            : [];
                                                                    } else {
                                                                        // Mark as needing initialization so useEffect will filter when channels load
                                                                        channelsInitializedRef.current.delete(`${selected.lineupId}-${packageId}`);
                                                                    }
                                                                }
                                                            }
                                                        } catch (error) {
                                                            console.error("Error parsing saved data:", error);
                                                        }
                                                    }
                                                }
                                            } else {
                                                // Reset package/addon for non-DirectTV providers
                                                setSelectedTvPackage(null);
                                                setSelectedTvAddons([]);
                                            }
                                        }
                                    }}
                                    placeholder="Select a provider"
                                    classNamePrefix="react-select"
                                    getOptionLabel={(option: LineupDetail) => option.name}
                                    getOptionValue={(option: LineupDetail) => option.lineupId}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: 40,
                                            borderRadius: "0.375rem",
                                            borderColor: "#d1d5db",
                                            "&:hover": { borderColor: "#9ca3af" },
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            zIndex: 50,
                                        }),
                                    }}
                                />
                                {errorMessage && (
                                    <div className={styles.errorMessage}>{errorMessage}</div>
                                )}
                            </div>

                            {/* Channel Selector (Bottom) - Only show if provider is selected */}
                            {selectedLineups.length > 0 && (
                                <div>
                                    {isLoadingChannels ? (
                                        <div style={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '40px 20px',
                                            color: 'gray'
                                        }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <CircularProgress size={40} style={{ marginBottom: '10px' }} />
                                                <div>Loading channels...</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <ChannelSelector
                                        onChange={() => {}}
                                        channel={channels}
                                        channeldata={savedChannelMap}
                                        lineups={selectedLineups}
                                        values={formValues}
                                        readOnly={viewMode === 'view'}
                                        change={(name: string, value: any) => {
                                            if (viewMode === 'view') return; // Don't allow changes in view mode
                                            setFormValues((prev) => ({
                                                ...prev,
                                                [name]: value,
                                            }));
                                        }}
                                        tvPackages={tvPackages}
                                        selectedTvPackage={selectedTvPackage}
                                        onTvPackageChange={viewMode === 'view' ? undefined : handleTvPackageChange}
                                        tvAddons={tvAddons}
                                        selectedTvAddons={selectedTvAddons}
                                        onTvAddonChange={viewMode === 'view' ? undefined : (addonIds: number[]) => {
                                            // Reset prevAddonsRef to trigger useEffect change detection
                                            prevAddonsRef.current = [...selectedTvAddons];
                                            setSelectedTvAddons(addonIds);
                                            
                                            // Immediately filter channels if available and package is selected
                                            // Use tvPackageChannels if available (has package/addon info), otherwise fallback to channels
                                            const channelsToUseForOnChange = tvPackageChannels.length > 0 ? tvPackageChannels : channels;
                                            if (selectedTvPackage && selectedTvPackage !== 7 && channelsToUseForOnChange.length > 0) {
                                                const allDitvLineups = selectedLineups.filter((lineup) => lineup.name?.startsWith("DIRECT"));
                                                if (allDitvLineups.length > 0) {
                                                    const previousAddonIds = selectedTvAddons;
                                                    const fChannels = filterChannelNumbers(channelsToUseForOnChange, selectedTvPackage, addonIds);
                                                    const previousFChannels = filterChannelNumbers(
                                                        channelsToUseForOnChange,
                                                        selectedTvPackage,
                                                        previousAddonIds
                                                    );
                                                    
                                                    // Only drop channels exclusive to unchecked addons; keep manual NFL picks
                                                    setSavedChannelMap((prev) => {
                                                        const updated = { ...prev };
                                                        allDitvLineups.forEach((lineup) => {
                                                            const matchedChannels = matchChannelsToLineup(fChannels, lineup.lineupId, lineup.name);
                                                            const previousMatched = matchChannelsToLineup(previousFChannels, lineup.lineupId, lineup.name);
                                                            updated[lineup.lineupId] = applyFilteredPackageChannels(
                                                                matchedChannels,
                                                                prev[lineup.lineupId] || [],
                                                                previousMatched
                                                            );
                                                            channelsInitializedRef.current.add(`${lineup.lineupId}-${selectedTvPackage}`);
                                                        });
                                                        return updated;
                                                    });
                                                    
                                                    setFormValues((prev) => {
                                                        const updatedChannels = { ...prev.channels };
                                                        allDitvLineups.forEach((lineup) => {
                                                            const matchedChannels = matchChannelsToLineup(fChannels, lineup.lineupId, lineup.name);
                                                            const previousMatched = matchChannelsToLineup(previousFChannels, lineup.lineupId, lineup.name);
                                                            updatedChannels[lineup.lineupId] = applyFilteredPackageChannels(
                                                                matchedChannels,
                                                                prev.channels[lineup.lineupId] || [],
                                                                previousMatched
                                                            );
                                                        });
                                                        return { ...prev, channels: updatedChannels };
                                                    });
                                                    
                                                    // Update refs
                                                    prevAddonsRef.current = [...addonIds];
                                                }
                                            }
                                        }}
                                        isLoadingTvPackages={isLoadingTvPackages}
                                    />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    </>
            </div>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {providerToDelete && selectedLineups.length === 1 ? (
                            <>
                                Are you sure you want to delete this Provider{' '}
                                <strong>&ldquo;{providerToDelete.name}&rdquo;</strong>? {DEACTIVATE_SCHEDULING_CONFIRM_MESSAGE}
                            </>
                        ) : (
                            <>
                                Are you sure you want to delete this Provider{' '}
                                {providerToDelete && <strong>&ldquo;{providerToDelete.name}&rdquo;</strong>}? This action cannot be undone.
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} style={{ color: 'black' }}>
                        Cancel
                    </Button>
                    <Button
                        style={{ backgroundColor: 'red', color: 'white' }}
                        onClick={handleConfirmDelete}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Rebuild Schedule Confirmation Dialog */}
            <Dialog
                open={isRebuildDialogOpen}
                onClose={() => setIsRebuildDialogOpen(false)}
                aria-labelledby="rebuild-dialog-title"
                aria-describedby="rebuild-dialog-description"
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle id="rebuild-dialog-title">Confirm Rebuild Schedule</DialogTitle>
                <DialogContent>
                    <DialogContentText id="rebuild-dialog-description">
                        Are you sure you want to rebuild the schedule? This will regenerate the schedule for future days, and for today from this time forward.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setIsRebuildDialogOpen(false)}
                        style={{ color: 'black' }}
                        disabled={isRebuilding}
                    >
                        Cancel
                    </Button>
                    <Button
                        style={{ backgroundColor: 'green', color: 'white' }}
                        onClick={handleRebuildSchedule}
                        disabled={isRebuilding}
                    >
                        {isRebuilding ? 'Rebuilding...' : 'Rebuild'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default GamemasterProvider;